// Непечатаемые знаки (¶ в конце абзаца, · пробел, ° неразрывный пробел, ↵ разрыв строки) и подсветка текущего
// блока в режиме «Фокус». Для больших документов декорации пересчитываются только в изменённых блоках.
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { uiStore } from '../../ui/uiState';

export const marksKey = new PluginKey<DecorationSet>('stoMarks');
const NBSP_CH = String.fromCharCode(0xa0);

function pilcrow() {
  const s = document.createElement('span');
  s.className = 'sto-pilcrow';
  s.textContent = '¶';
  s.setAttribute('contenteditable', 'false');
  return s;
}
function brMark() {
  const s = document.createElement('span');
  s.className = 'sto-brmark';
  s.textContent = '↵';
  return s;
}

/** Декорации одного текстового блока, начинающегося в pos. */
function blockDecos(node: PMNode, pos: number, out: Decoration[]) {
  node.forEach((child, off) => {
    const p = pos + 1 + off;
    if (child.isText) {
      const t = child.text || '';
      for (let i = 0; i < t.length; i++) {
        const ch = t[i];
        if (ch === ' ') out.push(Decoration.inline(p + i, p + i + 1, { class: 'sto-sp' }));
        else if (ch === NBSP_CH) out.push(Decoration.inline(p + i, p + i + 1, { class: 'sto-nbsp' }));
      }
    } else if (child.type.name === 'hardBreak') {
      out.push(Decoration.widget(p, brMark, { side: -1, key: 'br' }));
    }
  });
  out.push(Decoration.widget(pos + 1 + node.content.size, pilcrow, { side: 1, key: 'pil', marks: [] }));
}

function full(doc: PMNode): DecorationSet {
  const out: Decoration[] = [];
  doc.descendants((n, pos) => {
    if (n.isTextblock) { blockDecos(n, pos, out); return false; }
    return true;
  });
  return DecorationSet.create(doc, out);
}

/** Пересчёт только блоков, затронутых правкой. */
function incremental(tr: Transaction, prev: DecorationSet): DecorationSet {
  let set = prev.map(tr.mapping, tr.doc);
  const ranges: [number, number][] = [];
  tr.mapping.maps.forEach((map, i) => {
    map.forEach((_os, _oe, ns, ne) => {
      let a = ns; let b = ne;
      for (let k = i + 1; k < tr.mapping.maps.length; k++) { a = tr.mapping.maps[k].map(a, -1); b = tr.mapping.maps[k].map(b, 1); }
      ranges.push([a, b]);
    });
  });
  const doc = tr.doc;
  for (const [a, b] of ranges) {
    const from = Math.max(0, Math.min(a, doc.content.size));
    const to = Math.max(from, Math.min(b, doc.content.size));
    const out: Decoration[] = [];
    let lo = from; let hi = to;
    doc.nodesBetween(from, to, (n, pos) => {
      if (n.isTextblock) { blockDecos(n, pos, out); lo = Math.min(lo, pos); hi = Math.max(hi, pos + n.nodeSize); return false; }
      return true;
    });
    set = set.remove(set.find(lo, hi));
    set = set.add(doc, out);
  }
  return set;
}

let enabled = uiStore.getState().showMarks;

export const MarksExtension = Extension.create({
  name: 'stoMarks',
  addProseMirrorPlugins() {
    return [new Plugin<DecorationSet>({
      key: marksKey,
      state: {
        init: (_c, state: EditorState) => (enabled ? full(state.doc) : DecorationSet.empty),
        apply(tr, prev, _o, state) {
          const m = tr.getMeta(marksKey);
          if (m !== undefined) return m ? full(state.doc) : DecorationSet.empty;
          if (!enabled || !tr.docChanged) return prev;
          return incremental(tr, prev);
        },
      },
      props: {
        decorations: (s) => marksKey.getState(s),
        attributes: (): Record<string, string> => ({ class: enabled ? 'show-marks' : '' }),
      },
    })];
  },
});

// ------------------------------------------------------------------------------------------------ текущий блок («Фокус»)
export const focusKey = new PluginKey<DecorationSet>('stoFocusBlock');
let focusOn = uiStore.getState().focusMode;

function currentBlock(state: EditorState): DecorationSet {
  const $f = state.selection.$from;
  for (let d = $f.depth; d >= 1; d--) {
    const parent = $f.node(d - 1).type.name;
    if (parent === 'doc' || parent === 'appendix') {
      const pos = $f.before(d);
      return DecorationSet.create(state.doc, [Decoration.node(pos, pos + $f.node(d).nodeSize, { class: 'sto-cur-block' })]);
    }
  }
  return DecorationSet.empty;
}

export const FocusBlockExtension = Extension.create({
  name: 'stoFocusBlock',
  addProseMirrorPlugins() {
    return [new Plugin<DecorationSet>({
      key: focusKey,
      state: {
        init: (_c, s) => (focusOn ? currentBlock(s) : DecorationSet.empty),
        apply: (tr, prev, _o, s) => {
          if (tr.getMeta(focusKey) !== undefined || (focusOn && (tr.selectionSet || tr.docChanged))) return focusOn ? currentBlock(s) : DecorationSet.empty;
          return prev;
        },
      },
      props: { decorations: (s) => focusKey.getState(s) },
    })];
  },
});

/** Синхронизировать плагины с настройками вида (вызывается при изменении uiStore). */
export function syncMarks(editor: { view: any; isDestroyed: boolean } | null) {
  const s = uiStore.getState();
  const changed = s.showMarks !== enabled || s.focusMode !== focusOn;
  enabled = s.showMarks;
  focusOn = s.focusMode;
  if (!editor || editor.isDestroyed || !changed) return;
  editor.view.dispatch(editor.view.state.tr.setMeta(marksKey, enabled).setMeta(focusKey, focusOn).setMeta('addToHistory', false));
}
